import { CButton, CContainer } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilArrowLeft, cilCheckCircle, cilCopy } from '@coreui/icons'
import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { publicApi, apiErrorMessage } from '../../api/client'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { LedgerTimeline } from '../../components/public/LedgerTimeline'
import { StatusPill } from '../../components/common/StatusPill'
import { formatDateTime } from '../../utils/format'

export function LedgerDetailPage() {
  const { eventId = '' } = useParams()
  const query = useQuery({
    queryKey: ['public', 'ledger', eventId],
    queryFn: () => publicApi.ledgerEvent(eventId),
    enabled: Boolean(eventId),
  })
  return (
    <>
      <section className="page-hero page-hero--detail">
        <CContainer xxl>
          <Link to="/ledger" className="back-link">
            <CIcon icon={cilArrowLeft} /> All ledger records
          </Link>
          <span className="eyebrow">Permanent public record</span>
          <h1>{eventId}</h1>
        </CContainer>
      </section>
      <section className="section-space--tight">
        <CContainer xxl>
          {query.isLoading && <LoadingState />}
          {query.isError && <ErrorState message={apiErrorMessage(query.error)} />}
          {query.data && (
            <>
              <article className="record-detail">
                <div className="record-detail__header">
                  <div>
                    <span className={`category category-${query.data.event.category}`}>
                      {query.data.event.category}
                    </span>
                    <h2>{query.data.event.title}</h2>
                    <p>{query.data.event.summary}</p>
                  </div>
                  <div className="record-detail__verified">
                    <CIcon icon={cilCheckCircle} />
                    <strong>Hash-chained</strong>
                    <span>Sequence #{query.data.event.sequence}</span>
                  </div>
                </div>
                <dl className="record-grid">
                  <div>
                    <dt>Occurred</dt>
                    <dd>{formatDateTime(query.data.event.occurredAt)}</dd>
                  </div>
                  <div>
                    <dt>Published</dt>
                    <dd>{formatDateTime(query.data.event.publishedAt)}</dd>
                  </div>
                  <div>
                    <dt>Evidence source</dt>
                    <dd>{query.data.event.source}</dd>
                  </div>
                  <div>
                    <dt>Reviewer role</dt>
                    <dd>{query.data.event.reviewerRole}</dd>
                  </div>
                  <div>
                    <dt>Project</dt>
                    <dd>{query.data.event.projectAlias ?? 'Not publicly linked'}</dd>
                  </div>
                  <div>
                    <dt>Correction target</dt>
                    <dd>{query.data.event.correctionOf ?? 'Not a correction'}</dd>
                  </div>
                </dl>
                {(query.data.event.stateBefore || query.data.event.stateAfter) && (
                  <div className="state-transition">
                    <StatusPill state={query.data.event.stateBefore ?? 'unknown'} />
                    <span>→</span>
                    <StatusPill state={query.data.event.stateAfter ?? 'unknown'} />
                  </div>
                )}
                {query.data.event.details && (
                  <div className="record-section">
                    <h3>Public details</h3>
                    <p>{query.data.event.details}</p>
                  </div>
                )}
                <div className="record-section">
                  <h3>Evidence references</h3>
                  {query.data.event.evidence.length ? (
                    <ul>
                      {query.data.event.evidence.map((item, index) => (
                        <li key={`${item.label}-${index}`}>
                          <strong>{item.label}</strong>
                          <span>
                            {item.source}
                            {item.reference ? ` · ${item.reference}` : ''}
                            {item.checksum ? ` · checksum ${item.checksum}` : ''}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>
                      No public evidence reference was attached. Internal supporting evidence may remain
                      access-controlled.
                    </p>
                  )}
                </div>
                <div className="hash-box">
                  <div>
                    <span>Previous hash</span>
                    <code>{query.data.event.previousHash}</code>
                  </div>
                  <div>
                    <span>Event hash</span>
                    <code>{query.data.event.hash}</code>
                  </div>
                  <CButton
                    color="light"
                    onClick={() => void navigator.clipboard.writeText(query.data.event.hash)}
                  >
                    <CIcon icon={cilCopy} /> Copy hash
                  </CButton>
                </div>
              </article>
              {query.data.corrections.length > 0 && (
                <div className="mt-5">
                  <h2>Corrections linked to this record</h2>
                  <LedgerTimeline events={query.data.corrections} />
                </div>
              )}
            </>
          )}
        </CContainer>
      </section>
    </>
  )
}
